// Connections (SPEC 7.2): the machine and its state, this phone, alert health, and the two controls:
// Alerts (Automatic / Away / Quiet, with duration) and Remote control (On / Paused), with the machine's
// text abilities listed underneath. Turning text on happens at the desk.
import { useState } from "react";
import { useApp, useAppState } from "../app/AppContext";
import { useNow } from "../app/useNow";
import { PRODUCT_NAME } from "../brand";
import { agentCounts, machineStatus, type ThemePref } from "../lib/model";
import { enablePushFailureText } from "../lib/push";
import { ago, clock, untilText } from "../lib/time";
import { Banner } from "../components/Banner";
import { ConnectionChip, Page, TabBar, TopBar } from "../components/Shell";

const DURATIONS: { label: string; ms: number | null }[] = [
  { label: "1 hour", ms: 60 * 60_000 },
  { label: "2 hours", ms: 2 * 60 * 60_000 },
  { label: "4 hours", ms: 4 * 60 * 60_000 },
  { label: "Until I change it", ms: null },
];

export function Connections() {
  const s = useAppState();
  const { controller } = useApp();
  const now = useNow(5000);
  const [pendingMode, setPendingMode] = useState<"away" | "quiet" | null>(null);
  const [confirmUnpair, setConfirmUnpair] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushNote, setPushNote] = useState<string | null>(null);

  const machine = s.machine?.name ?? s.pairing?.machine.name ?? "Your PC";
  const ms = machineStatus(s);
  const counts = agentCounts(s.agents);
  const alerts = s.settings?.alerts;
  const canControl = s.conn === "connected" && ms === "online" && !s.removed;

  const pickMode = (mode: "auto" | "away" | "quiet") => {
    if (mode === "auto") {
      setPendingMode(null);
      controller.setAlerts("auto");
    } else setPendingMode(mode);
  };
  const pickDuration = (ms: number | null) => {
    if (!pendingMode) return;
    controller.setAlerts(pendingMode, ms ? now + ms : undefined);
    setPendingMode(null);
  };

  const turnOnAlerts = async () => {
    setPushBusy(true);
    setPushNote(null);
    const r = await controller.enablePush();
    setPushBusy(false);
    if (r.ok) setPushNote("Alerts are on. To test from the desk, set Alerts to Away first (Automatic stays quiet while you are at the keyboard), then lock the phone and send a test question.");
    else setPushNote(enablePushFailureText(r.why, r.health.lastError));
  };

  return (
    <>
      <TopBar right={<ConnectionChip />} />
      <Page>
        <h2 className="label">Connections</h2>

        {s.removed && (
          <Banner tone="danger" icon="lock" title="This phone was removed.">
            Run <code>whistle pair</code> at the desk and scan the new code. Paying does not restore the right to answer.
            {!s.removedByMachine && <> To delete what this phone still keeps for {machine}, tap Forget this pairing below.</>}
          </Banner>
        )}

        <section className="panel" aria-labelledby="m-head">
          <div className="head">
            <b id="m-head">{machine}</b>
            {ms === "online" ? (
              <span className="chip chip-ok">
                <span className="mark mark-online" aria-hidden="true" />
                Online
              </span>
            ) : ms === "unreachable" ? (
              <span className="chip chip-danger">
                <span className="mark mark-off" aria-hidden="true" />
                Unreachable
              </span>
            ) : (
              <span className="chip chip-unknown">Unknown</span>
            )}
          </div>
          <dl className="kv">
            <dt className="k">Last seen</dt>
            <dd className="v num">{ms === "online" ? "now" : s.machineLastSeen ? `${clock(s.machineLastSeen)} · ${ago(s.machineLastSeen, now)} ago` : "not yet"}</dd>
            <dt className="k">Environments</dt>
            <dd className="v">{s.machine?.envs.map((e) => e.label).join(", ") || "—"}</dd>
            <dt className="k">Agents</dt>
            <dd className="v">
              {s.agents.length === 0 ? "none running" : `${s.agents.length}${counts.blocked ? ` · ${counts.blocked} waiting` : ""}${counts.working ? ` · ${counts.working} working` : ""}`}
            </dd>
            <dt className="k">Relay</dt>
            <dd className="v">{s.conn === "connected" ? "connected" : s.conn === "connecting" || s.conn === "reconnecting" ? "connecting" : s.online ? "not connected" : "offline"}</dd>
          </dl>
          <div className="switch">
            <div className="t">
              <b>Remote control</b>
              <span>{s.settings?.remotePaused ? `Paused. Cards from ${machine} are read-only.` : `On. Answers from this phone reach ${machine}.`}</span>
            </div>
            <button type="button" role="switch" aria-checked={!s.settings?.remotePaused} aria-label="Remote control" className="toggle" disabled={!canControl || !s.settings} onClick={() => controller.setRemote(!s.settings?.remotePaused)} />
          </div>
          <div className="help">
            Replies {s.settings?.textReplies ? "on" : "off"} · Messages off
            {s.settings?.textReplies ? "" : " · turn replies on at the desk with "}
            {s.settings?.textReplies ? "" : <code>whistle text replies on</code>}
            <br />
            <span className="faint">Messages to idle agents are waiting on herdr support for a conditional send.</span>
          </div>
        </section>

        <section className="panel" aria-labelledby="a-head">
          <div className="head">
            <b id="a-head">Alerts</b>
            <span className="small muted">applies to {machine}</span>
          </div>
          <div className="stack">
            <div className="seg" role="group" aria-label="Alert mode">
              {(["auto", "away", "quiet"] as const).map((m) => (
                <button key={m} type="button" aria-pressed={(pendingMode ?? alerts?.mode) === m} disabled={!canControl || !alerts} onClick={() => pickMode(m)}>
                  {m === "auto" ? "Automatic" : m === "away" ? "Away" : "Quiet"}
                </button>
              ))}
            </div>
            {pendingMode ? (
              <div className="stack">
                <span className="small muted">{pendingMode === "away" ? "Away: buzz this phone even when you're at the desk. For how long?" : "Quiet: no buzzes at all. For how long?"}</span>
                <div className="btn-row">
                  {DURATIONS.map((d) => (
                    <button key={d.label} type="button" className="btn btn-ghost btn-sm" onClick={() => pickDuration(d.ms)}>
                      {d.label}
                    </button>
                  ))}
                </div>
                <button type="button" className="btn btn-quiet btn-sm" onClick={() => setPendingMode(null)}>
                  Keep {alerts?.mode === "auto" ? "Automatic" : alerts?.mode === "away" ? "Away" : "Quiet"}
                </button>
              </div>
            ) : (
              <p className="help">
                {alerts?.mode === "auto" && "Automatic: buzzes when you're away from the keyboard, stays quiet at the desk."}
                {alerts?.mode === "away" && `Away ${untilText(alerts.until, now) || "until you change it"}: buzzes even at the desk.`}
                {alerts?.mode === "quiet" && `Quiet ${untilText(alerts.until, now) || "until you change it"}: no buzzes. The inbox still updates.`}
                {!alerts && "Waiting for the machine's settings."}
              </p>
            )}
          </div>
        </section>

        <section className="panel" aria-labelledby="p-head">
          <div className="head">
            <b id="p-head">This phone</b>
            {!s.push.supported ? (
              <span className="chip chip-unknown">No push</span>
            ) : s.push.permission === "granted" && s.push.subscribed ? (
              <span className="chip chip-ok">Alerts on</span>
            ) : (
              <span className="chip chip-danger">Alerts off</span>
            )}
          </div>
          <dl className="kv">
            <dt className="k">Name</dt>
            <dd className="v">{s.pairing?.deviceName ?? "—"}</dd>
            <dt className="k">Paired</dt>
            <dd className="v">{s.pairing ? new Date(s.pairing.pairedAt).toLocaleDateString() : "not paired"}</dd>
            <dt className="k">Relay</dt>
            <dd className="v mono small">{s.pairing ? s.pairing.relayOrigin.replace(/^https?:\/\//, "") : "—"}</dd>
          </dl>
          <div className="stack">
            {!s.push.supported ? (
              <p className="help">This browser can't receive push alerts. On Android, open the app in Chrome or Samsung Internet and add it to the Home screen.</p>
            ) : s.push.permission === "denied" ? (
              <div className="help">
                <b>Notifications are blocked for this site.</b>
                <ol>
                  <li>Open the browser menu, then Site settings (or the lock icon next to the address).</li>
                  <li>Set Notifications to Allowed.</li>
                  <li>Come back here and tap Turn on alerts.</li>
                </ol>
                <button type="button" className="btn btn-ghost btn-sm" disabled={pushBusy} onClick={turnOnAlerts}>
                  Turn on alerts
                </button>
              </div>
            ) : s.push.permission === "granted" && s.push.subscribed ? (
              <p className="help">
                {s.push.registered ? "Registered with the relay this session." : "Registers with the relay on the next connection."} To check delivery from the desk, set Alerts to Away, lock the phone and send a test question.
              </p>
            ) : (
              <>
                <p className="help">Alerts tell you when an agent is waiting, even with the phone locked. They say how many agents need an answer, never the question.</p>
                <button type="button" className="btn btn-primary" disabled={pushBusy || !s.pairing} onClick={turnOnAlerts}>
                  {pushBusy ? "Turning on…" : "Turn on alerts"}
                </button>
              </>
            )}
            {pushNote && (
              <p className="help" role="status">
                {pushNote}
              </p>
            )}
          </div>
        </section>

        <section className="panel" aria-labelledby="l-head">
          <div className="head">
            <b id="l-head">Appearance</b>
          </div>
          <div className="stack">
            <div className="seg" role="group" aria-label="Theme">
              {(["system", "light", "dark"] as ThemePref[]).map((t) => (
                <button key={t} type="button" aria-pressed={s.prefs.theme === t} onClick={() => controller.setPrefs({ theme: t })}>
                  {t === "system" ? "System" : t === "light" ? "Light" : "Dark"}
                </button>
              ))}
            </div>
            <div className="switch">
              <div className="t">
                <b>Vibrate on hold and send</b>
                <span>A short pulse when the 2-second count starts and when an answer leaves.</span>
              </div>
              <button type="button" role="switch" aria-checked={s.prefs.haptics} aria-label="Vibrate on hold and send" className="toggle" onClick={() => controller.setPrefs({ haptics: !s.prefs.haptics })} />
            </div>
          </div>
        </section>

        <section className="panel" aria-labelledby="u-head">
          <div className="head">
            <b id="u-head">Pairing</b>
          </div>
          <div className="stack">
            <p className="help">
              To pair another PC or re-pair this one, run <code>whistle pair</code> at the desk and scan the code. v0.1 pairs one phone with one PC.
            </p>
            {s.pairing &&
              (confirmUnpair ? (
                <div className="btn-row">
                  <button type="button" className="btn btn-ghost" onClick={() => setConfirmUnpair(false)}>
                    Keep it
                  </button>
                  <button type="button" className="btn btn-danger" onClick={() => void controller.unpair()}>
                    Forget {machine}
                  </button>
                </div>
              ) : (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmUnpair(true)}>
                  Forget this pairing
                </button>
              ))}
            <p className="help faint">
              {PRODUCT_NAME} v0.1 · Your PC decides. This phone can only answer a question that's waiting right now, with the choices it offers.
            </p>
          </div>
        </section>
      </Page>
      <TabBar />
    </>
  );
}
